/**
 * Unified Team Management page with tabs for Users, Teams, and RBAC
 */

import React, { useState } from 'react';
import { Users, UserCog, Shield } from 'lucide-react';
import { UserList } from '../Users/UserList';
import { TeamMembers } from './TeamMembers';
import { RolesManager } from '../RBAC/RolesManager';
import { Card } from '../UI/Card';

type Tab = 'users' | 'teams' | 'rbac';

export function TeamManagement() {
  const [activeTab, setActiveTab] = useState<Tab>('users');

  const tabs = [
    { id: 'users' as Tab, label: 'Users', icon: Users },
    { id: 'teams' as Tab, label: 'Teams', icon: UserCog },
    { id: 'rbac' as Tab, label: 'Roles & Permissions', icon: Shield },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Team Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage users, teams, and permissions</p>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <div className="border-b border-gray-200 dark:border-slate-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${
                      isActive
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'users' && <UserList />}
          {activeTab === 'teams' && <TeamMembers />}
          {activeTab === 'rbac' && <RolesManager />}
        </div>
      </Card>
    </div>
  );
}

